import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe, TrendingUp, TrendingDown, Users, Clock, MousePointerClick, AlertCircle, Activity } from "lucide-react";
import axios from "axios";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import PageDateFilter from "@/components/PageDateFilter";
import TrafficComparisonTable from "@/components/TrafficComparisonTable";
import RegionalTrafficTables from "@/components/RegionalTrafficTables";
import PageNote from "@/components/PageNote";

const WebsiteTrafficPage = () => {
  const { sessionToken, isSuperAdmin } = useAuth();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskNumber, maskPercent } = usePresentationMode();
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [timeGranularity, setTimeGranularity] = useState("day"); // day, week, month
  const [excludeTurkey, setExcludeTurkey] = useState(true); // Default to excluding Turkey traffic
  const [showTurkeyExclusionAlert, setShowTurkeyExclusionAlert] = useState(true); // Toggle for Turkey exclusion alert visibility
  const [trafficData, setTrafficData] = useState(null);
  const [trafficSourcesData, setTrafficSourcesData] = useState(null);
  const [previousPeriodData, setPreviousPeriodData] = useState(null); // For comparison
  const [previousSourcesData, setPreviousSourcesData] = useState(null);
  const [comparisonMode, setComparisonMode] = useState("prior"); // "none", "prior", "yoy"
  const showComparison = comparisonMode !== "none";
  const [error, setError] = useState(null);
  const [trafficDataSource, setTrafficDataSource] = useState(null); // null until loaded, then "ga4" or "hubspot"
  const [settingsLoaded, setSettingsLoaded] = useState(false); // Track if settings have been fetched
  const [aiReferralData, setAiReferralData] = useState({
    true_ai_referrals: 0,
    period_label: "",
    last_updated: null
  });
  const [showTrueAiReferrals, setShowTrueAiReferrals] = useState(true); // Toggle for True AI Referrals display
  
  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  // sessionStorage changes don't trigger React re-renders, so we need this state
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Listen for tenant changes via storage events and visibility changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
        // Reset settings to force re-fetch
        setSettingsLoaded(false);
        setTrafficDataSource(null);
      }
    };
    
    // Check on visibility change (when user navigates back to this tab/page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTenantChange();
      }
    };
    
    // Check periodically for tenant changes (handles navigation within app)
    const interval = setInterval(checkTenantChange, 500);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', checkTenantChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', checkTenantChange);
    };
  }, [currentTenantId]);

  // Nav dark color for consistent styling
  const NAV_DARK_COLOR = "#0F172A";

  // Helper function to aggregate data by week or month
  const aggregateByGranularity = (data, granularity) => {
    if (!data || data.length === 0 || granularity === "day") return data;
    
    const aggregated = {};
    
    data.forEach(item => {
      let key;
      let fullDateKey;
      
      // Use full_date if available (YYYY-MM-DD format), otherwise parse from date (MM/DD)
      let date;
      if (item.full_date) {
        date = new Date(item.full_date);
      } else if (item.date.includes('/')) {
        // MM/DD format - use current year as fallback (data should have full_date now)
        const [month, day] = item.date.split('/');
        const currentYear = new Date().getFullYear();
        date = new Date(currentYear, parseInt(month) - 1, parseInt(day));
      } else if (item.date.includes('-')) {
        date = new Date(item.date);
      } else {
        return; // Skip invalid dates
      }
      
      if (granularity === "week") {
        // Get week start (Sunday)
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = `${(weekStart.getMonth() + 1).toString().padStart(2, '0')}/${weekStart.getDate().toString().padStart(2, '0')}`;
        fullDateKey = `${weekStart.getFullYear()}-${(weekStart.getMonth() + 1).toString().padStart(2, '0')}-${weekStart.getDate().toString().padStart(2, '0')}`;
      } else if (granularity === "month") {
        key = `${(date.getMonth() + 1).toString().padStart(2, '0')}/01`;
        fullDateKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-01`;
      }
      
      if (!aggregated[fullDateKey]) {
        aggregated[fullDateKey] = { date: key, full_date: fullDateKey, organic: 0, organic_social: 0, paid: 0, direct: 0, email: 0, referral: 0, ai_referral: 0 };
      }
      
      // Sum up values
      aggregated[fullDateKey].organic += item.organic || 0;
      aggregated[fullDateKey].organic_social += item.organic_social || 0;
      aggregated[fullDateKey].paid += item.paid || 0;
      aggregated[fullDateKey].direct += item.direct || 0;
      aggregated[fullDateKey].email += item.email || 0;
      aggregated[fullDateKey].referral += item.referral || 0;
      aggregated[fullDateKey].ai_referral += item.ai_referral || 0;
    });
    
    // Sort by full_date (YYYY-MM-DD) for proper chronological order
    return Object.values(aggregated).sort((a, b) => {
      const aDate = a.full_date || a.date;
      const bDate = b.full_date || b.date;
      return aDate.localeCompare(bDate);
    });
  };

  // Fetch AI referral tracking data and traffic data source from tenant settings
  // CRITICAL: Now includes currentTenantId as dependency to re-fetch when tenant changes
  useEffect(() => {
    const fetchTenantSettings = async () => {
      // Use the state variable which is synced with sessionStorage
      const viewingTenantId = currentTenantId;
      
      console.log("=== FETCHING TENANT SETTINGS ===");
      console.log("currentTenantId (from state):", viewingTenantId);
      console.log("sessionToken exists:", !!sessionToken);
      
      if (!sessionToken) {
        console.log("No session token yet, waiting...");
        return;
      }
      
      // If no tenant ID yet, use default settings and mark as loaded
      if (!viewingTenantId) {
        console.log("No tenant ID yet, using default settings");
        setTrafficDataSource("hubspot"); // Default to hubspot
        setSettingsLoaded(true);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/tenants/${viewingTenantId}/display-settings`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
        
        console.log("Settings response for tenant", viewingTenantId, ":", response.data);
        
        const aiData = response.data?.ai_referral_tracking || {};
        setAiReferralData(aiData);
        
        // Get traffic data source setting
        const dataSource = response.data?.traffic_data_source || "hubspot";
        console.log("traffic_data_source from API:", response.data?.traffic_data_source);
        console.log("Setting trafficDataSource to:", dataSource);
        
        // Get show_true_ai_referrals toggle (default to true if not set)
        const showTrueAi = response.data?.show_true_ai_referrals !== false;
        setShowTrueAiReferrals(showTrueAi);
        
        // Get show_turkey_exclusion_alert toggle (default to true if not set)
        const showTurkeyAlert = response.data?.show_turkey_exclusion_alert !== false;
        setShowTurkeyExclusionAlert(showTurkeyAlert);
        
        setTrafficDataSource(dataSource);
        setSettingsLoaded(true);
      } catch (error) {
        console.error("Error fetching tenant settings:", error);
        setTrafficDataSource("hubspot"); // Default fallback
        setSettingsLoaded(true);
      }
    };
    
    // Add small delay to ensure state is updated after navigation
    const timer = setTimeout(() => {
      fetchTenantSettings();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [sessionToken, currentTenantId]); // CRITICAL: Added currentTenantId as dependency

  const handleDateChange = useCallback((dateInfo) => {
    console.log("Website Traffic date filter changed:", dateInfo);
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  // Calculate previous period dates based on current period and comparison mode
  const getPreviousPeriodDates = (startDate, endDate, mode = comparisonMode) => {
    if (!startDate || !endDate) return { prevStart: null, prevEnd: null };
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (mode === "yoy") {
      // Year-over-year: same dates, one year prior
      const prevStart = new Date(start);
      prevStart.setFullYear(prevStart.getFullYear() - 1);
      const prevEnd = new Date(end);
      prevEnd.setFullYear(prevEnd.getFullYear() - 1);
      return {
        prevStart: prevStart.toISOString().split('T')[0],
        prevEnd: prevEnd.toISOString().split('T')[0]
      };
    }
    
    // Prior period: immediately preceding period of same length
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - daysDiff + 1);
    
    return {
      prevStart: prevStart.toISOString().split('T')[0],
      prevEnd: prevEnd.toISOString().split('T')[0]
    };
  };

  const fetchTrafficData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use currentTenantId from state (which is kept in sync with sessionStorage)
      const viewingTenantId = currentTenantId;
      
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;
      if (selectedRegion && selectedRegion !== "all") params.region = selectedRegion;
      // Add Turkey exclusion parameter
      params.exclude_turkey = excludeTurkey;

      // Determine which API to use based on tenant setting
      const useHubSpot = trafficDataSource === "hubspot";
      const sourcesEndpoint = useHubSpot 
        ? `${API}/integrations/analytics/hubspot/traffic-sources`
        : `${API}/integrations/analytics/ga4/traffic-sources`;
      const trafficEndpoint = useHubSpot
        ? `${API}/integrations/analytics/hubspot/traffic`
        : `${API}/integrations/analytics/ga4/traffic`;

      console.log("=== TRAFFIC DATA FETCH ===");
      console.log("trafficDataSource state:", trafficDataSource);
      console.log("useHubSpot:", useHubSpot);
      console.log("sourcesEndpoint:", sourcesEndpoint);
      console.log("trafficEndpoint:", trafficEndpoint);
      console.log("currentTenantId (from state):", viewingTenantId);
      console.log("params:", params);
      
      // Calculate previous period dates for comparison
      const { prevStart, prevEnd } = getPreviousPeriodDates(dateFilter?.startDate, dateFilter?.endDate);
      
      // Fetch traffic data - handle each request separately to avoid one failure breaking both
      let trafficResponse = null;
      let sourcesResponse = null;
      let prevTrafficResponse = null;
      let prevSourcesResponse = null;
      
      try {
        trafficResponse = await axios.get(trafficEndpoint, { params });
        console.log("Traffic data response:", trafficResponse.data);
      } catch (err) {
        console.error("Error fetching traffic:", err);
      }
      
      let sourcesError = null;
      try {
        sourcesResponse = await axios.get(sourcesEndpoint, { params });
        console.log(`${useHubSpot ? 'HubSpot' : 'GA4'} traffic sources response:`, sourcesResponse.data);
        
        // Check if response contains an error field (backend returns error in response body)
        if (sourcesResponse?.data?.error) {
          sourcesError = sourcesResponse.data.error;
          console.error("Sources API returned error:", sourcesError);
        }
      } catch (err) {
        console.error("Error fetching sources:", err);
        sourcesError = err.response?.data?.detail || err.message || "Failed to fetch sources data";
      }
      
      // Fetch previous period data for comparison
      if (prevStart && prevEnd && showComparison) {
        const prevParams = { ...params, start_date: prevStart, end_date: prevEnd };
        try {
          prevTrafficResponse = await axios.get(trafficEndpoint, { params: prevParams });
          prevSourcesResponse = await axios.get(sourcesEndpoint, { params: prevParams });
        } catch (err) {
          console.error("Error fetching previous period data:", err);
        }
      }
      
      // Set traffic data if available
      if (trafficResponse?.data) {
        const ga4Data = trafficResponse.data;
        setTrafficData({
          data: ga4Data.byDate || [],
          summary: {
            total_sessions: ga4Data.sessions || 0,
            total_users: ga4Data.users || 0,
            total_page_views: ga4Data.pageViews || 0,
            avg_bounce_rate: ga4Data.bounceRate || 0,
            avg_session_duration: ga4Data.avgSessionDuration || 0
          }
        });
      } else {
        setTrafficData({ data: [], summary: {} });
      }
      
      // Set sources data if available (check for backend error in response)
      if (sourcesResponse?.data && !sourcesResponse.data.error) {
        setTrafficSourcesData(sourcesResponse.data);
      } else {
        setTrafficSourcesData({ byDate: [], totals: {} });
      }
      
      // Set previous period data for comparison
      if (prevTrafficResponse?.data) {
        const prevGa4Data = prevTrafficResponse.data;
        setPreviousPeriodData({
          data: prevGa4Data.byDate || [],
          summary: {
            total_sessions: prevGa4Data.sessions || 0,
            total_users: prevGa4Data.users || 0,
            total_page_views: prevGa4Data.pageViews || 0,
            avg_bounce_rate: prevGa4Data.bounceRate || 0,
            avg_session_duration: prevGa4Data.avgSessionDuration || 0
          }
        });
      } else {
        setPreviousPeriodData(null);
      }
      
      if (prevSourcesResponse?.data) {
        setPreviousSourcesData(prevSourcesResponse.data);
      } else {
        setPreviousSourcesData(null);
      }
      
      // Show error if HubSpot is selected but sources failed
      if (useHubSpot && sourcesError) {
        // Parse the error to give a user-friendly message
        let friendlyError = sourcesError;
        if (sourcesError.includes("BAD_CLIENT_ID") || sourcesError.includes("missing or unknown client id")) {
          friendlyError = "HubSpot OAuth credentials are invalid or not configured. Please contact your administrator to update HubSpot API credentials.";
        } else if (sourcesError.includes("token refresh failed")) {
          friendlyError = "HubSpot connection expired. Please reconnect HubSpot in Admin > Integrations.";
        }
        setError(`⚠️ HubSpot Data Unavailable: ${friendlyError}`);
      }
      // Show error only if both failed
      else if (!trafficResponse?.data && !sourcesResponse?.data) {
        const errorMsg = useHubSpot 
          ? "HubSpot not connected or missing business-intelligence scope. Please check Admin > Integrations."
          : "GA4 not connected. Please connect GA4 in Admin > Integrations.";
        setError(errorMsg);
      }
    } catch (error) {
      console.error("Error fetching traffic data:", error);
      setError("Failed to load traffic data. Please check your connection.");
      setTrafficData({ data: [], summary: {} });
      setTrafficSourcesData({ byDate: [], totals: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Wait for both date filter AND settings to be loaded
    if (!dateFilterInitialized || !settingsLoaded) return;
    console.log("Triggering fetchTrafficData - tenant:", currentTenantId, "dataSource:", trafficDataSource);
    fetchTrafficData();
  }, [sessionToken, dateFilterInitialized, settingsLoaded, dateFilter?.startDate, dateFilter?.endDate, selectedRegion, excludeTurkey, trafficDataSource, currentTenantId, comparisonMode]); // CRITICAL: Added currentTenantId and comparisonMode

  // Calculate metrics from data - with safe defaults
  const snapshots = trafficData?.data || [];
  const summary = trafficData?.summary || {};
  
  const totalSessions = summary.total_sessions || 0;
  const totalUsers = summary.total_users || 0;
  const totalPageViews = summary.total_page_views || 0;
  const avgBounceRate = summary.avg_bounce_rate || 0;
  const avgSessionDuration = summary.avg_session_duration || 0;

  // Transform data for charts - handle both old format and new GA4 format
  const trafficOverTime = (snapshots || []).map(s => ({
    date: s?.date || 'Unknown',
    sessions: s?.sessions || 0,
    users: s?.users || 0,
    pageViews: s?.page_views || s?.pageViews || 0
  }));

  // Get traffic by source for stacked area chart - with safe defaults
  const trafficBySource = trafficSourcesData?.byDate || [];
  const sourceTotals = trafficSourcesData?.totals || {};

  // Format session duration as mm:ss
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Traffic source colors - use tenant's chart colors if available
  const SOURCE_COLORS = {
    organic: chartColors?.[0] || "#10B981",           // Primary color or Green
    organic_social: "#14B8A6",                        // Teal for Organic Social
    paid: chartColors?.[2] || "#F59E0B",              // Third color or Amber
    paid_social: "#F97316",                           // Orange for Paid Social (NEW)
    direct: chartColors?.[1] || "#3B82F6",            // Secondary color or Blue
    email: chartColors?.[3] || "#8B5CF6",             // Fourth color or Purple
    referral: chartColors?.[4] || "#EC4899",          // Fifth color or Pink
    ai_referral: "#06B6D4",                           // Cyan for AI referral
    other_campaigns: "#6366F1",                       // Indigo for Other Campaigns (NEW)
    offline: "#78716C",                               // Stone for Offline (NEW)
    display: "#EF4444",                               // Red for Display
    unassigned: "#9CA3AF"                             // Gray for Unassigned
  };

  // Comparison label helper
  const comparisonLabel = comparisonMode === "yoy" ? "vs year ago" : "vs prior";
  const calcPercentChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Show initial loading while waiting for date filter to initialize
  if (!dateFilterInitialized) {
    return (
      <div className="animate-fade-in" data-testid="website-traffic-page">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{pageTitles?.website_traffic || "Website Traffic"}</h1>
            <p className="text-sm text-slate-500 mt-1">
              Traffic insights and keyword performance
            </p>
          </div>
          <PageDateFilter 
            pageId="website-traffic" 
            defaultPreset="last_30_days"
            onDateChange={handleDateChange}
          />
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-fade-in" data-testid="website-traffic-page">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{pageTitles?.website_traffic || "Website Traffic"}</h1>
            <p className="text-sm text-slate-500 mt-1">
              Traffic insights and keyword performance
            </p>
          </div>
          <PageDateFilter 
            pageId="website-traffic" 
            defaultPreset="last_30_days"
            onDateChange={handleDateChange}
          />
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="website-traffic-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Website Traffic</h1>
          <p className="text-sm text-slate-500 mt-1">
            Traffic insights and keyword performance
            {dateFilter?.startDate && dateFilter?.endDate && (
              <span className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded">
                {dateFilter.startDate} to {dateFilter.endDate}
              </span>
            )}
            {/* Data Source Indicator */}
            <span className={`ml-2 text-xs px-2 py-1 rounded font-medium ${
              trafficDataSource === "hubspot" 
                ? "bg-orange-100 text-orange-700" 
                : "bg-blue-100 text-blue-700"
            }`}>
              {trafficDataSource === "hubspot" ? "📊 HubSpot Data" : "📈 GA4 Data"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Day/Week/Month Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setTimeGranularity("day")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeGranularity === "day" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setTimeGranularity("week")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeGranularity === "week" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setTimeGranularity("month")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeGranularity === "month" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Month
            </button>
          </div>
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-40">
              <Globe className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="na">North America</SelectItem>
              <SelectItem value="latam">LATAM</SelectItem>
              <SelectItem value="emea">EMEA</SelectItem>
              <SelectItem value="apac">APAC</SelectItem>
            </SelectContent>
          </Select>
          {/* Turkey Exclusion Toggle - Only visible to Super Admins */}
          {isSuperAdmin && (
            <label className="flex items-center gap-2 text-sm text-slate-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
              <input
                type="checkbox"
                checked={excludeTurkey}
                onChange={(e) => setExcludeTurkey(e.target.checked)}
                className="rounded border-red-300 text-red-600"
              />
              <span className="text-red-700">Exclude Turkey</span>
            </label>
          )}
          {/* Comparison Mode Toggle */}
          <Select value={comparisonMode} onValueChange={setComparisonMode}>
            <SelectTrigger className="w-[160px]" data-testid="comparison-mode-toggle">
              <SelectValue placeholder="Comparison" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Comparison</SelectItem>
              <SelectItem value="prior">vs Prior Period</SelectItem>
              <SelectItem value="yoy">vs Year Ago</SelectItem>
            </SelectContent>
          </Select>
          <PageDateFilter 
            pageId="website-traffic" 
            defaultPreset="last_30_days"
            onDateChange={handleDateChange}
          />
        </div>
      </div>

      {/* Page Notes & Market Insights */}
      <PageNote pageId="website_traffic" />

      {/* Turkey Exclusion Notice - Only visible to Super Admins when alert is enabled */}
      {isSuperAdmin && excludeTurkey && showTurkeyExclusionAlert && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span><strong>Turkey traffic excluded</strong> - Data shown excludes all sessions from Turkey. Uncheck the toggle above to include Turkey traffic.</span>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-yellow-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Source Indicator */}
      <Card className={`mb-4 ${trafficDataSource === "hubspot" ? "border-orange-200 bg-orange-50" : "border-amber-200 bg-amber-50"}`}>
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${trafficDataSource === "hubspot" ? "text-orange-600" : "text-amber-600"}`} />
            <div>
              <p className={`font-medium ${trafficDataSource === "hubspot" ? "text-orange-800" : "text-amber-800"}`}>
                <strong>Data Source:</strong> {trafficDataSource === "hubspot" ? "HubSpot Analytics" : "Google Analytics 4"}
              </p>
              <p className={`text-sm mt-1 ${trafficDataSource === "hubspot" ? "text-orange-700" : "text-amber-700"}`}>
                {trafficDataSource === "hubspot" ? (
                  <>
                    <strong>Traffic sources are from HubSpot.</strong> HubSpot typically reports higher session counts than GA4 due to its more comprehensive tracking.
                  </>
                ) : (
                  <>
                    <strong>Session counts will differ from HubSpot.</strong> HubSpot&apos;s tracking captures additional sessions 
                    that GA4 cannot attribute, including traffic from users who block GA4 tracking, direct visits where 
                    HubSpot can identify the source via its own cookie, and cross-device journeys. 
                    HubSpot typically reports <strong>significantly higher session counts</strong> than GA4 for the same period.
                  </>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Referral Tracking Alert - Only show when using GA4, not HubSpot */}
      {trafficDataSource === "ga4" && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">About AI Referral Traffic Tracking</p>
                <p className="text-sm text-blue-700 mt-1">
                  AI referral traffic is challenging to track accurately. GA4 captures some AI sources (ChatGPT, Perplexity, Claude, etc.), 
                  but HubSpot often detects additional AI referral traffic that is not visible in GA4. 
                  To ensure data accuracy, <strong>True AI Referral traffic is updated weekly by system admins</strong> based on HubSpot&apos;s more comprehensive tracking.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Sessions</p>
                <p className="kpi-value">{maskNumber(isPresentationMode ? '***' : totalSessions)}</p>
                {showComparison && previousPeriodData?.summary?.total_sessions !== undefined && !isPresentationMode ? (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(totalSessions, previousPeriodData.summary.total_sessions);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? (
                            <TrendingUp className="w-3 h-3 text-green-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+' : ''}{change}%
                          </span>
                          <span className="text-xs text-slate-400">{comparisonLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="kpi-change text-slate-500 text-xs">
                    {isPresentationMode ? `**% ${comparisonLabel}` : (dateFilter?.label || 'Selected period')}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#00A5A8]/10 flex items-center justify-center">
                <Globe className="w-6 h-6 text-[#00A5A8]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Users</p>
                <p className="kpi-value">{maskNumber(isPresentationMode ? '***' : totalUsers)}</p>
                {showComparison && previousPeriodData?.summary?.total_users !== undefined && !isPresentationMode ? (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(totalUsers, previousPeriodData.summary.total_users);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? (
                            <TrendingUp className="w-3 h-3 text-green-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+' : ''}{change}%
                          </span>
                          <span className="text-xs text-slate-400">{comparisonLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="kpi-change text-slate-500 text-xs">
                    {dateFilter?.label || 'Selected period'}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Page Views</p>
                <p className="kpi-value">{maskNumber(totalPageViews)}</p>
                {showComparison && previousPeriodData?.summary?.total_page_views !== undefined ? (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(totalPageViews, previousPeriodData.summary.total_page_views);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? (
                            <TrendingUp className="w-3 h-3 text-green-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+' : ''}{change}%
                          </span>
                          <span className="text-xs text-slate-400">{comparisonLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="kpi-change text-slate-500 text-xs">
                    {dateFilter?.label || 'Selected period'}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Referral Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* AI Referrals from HubSpot/GA4 */}
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">AI Referrals ({trafficDataSource === "hubspot" ? "HubSpot" : "GA4"})</p>
                <p className="kpi-value" style={{ color: SOURCE_COLORS.ai_referral }}>{maskNumber((sourceTotals.ai_referral || 0))}</p>
                {showComparison && previousSourcesData?.totals?.ai_referral !== undefined ? (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.ai_referral || 0, previousSourcesData.totals.ai_referral || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? (
                            <TrendingUp className="w-3 h-3 text-green-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+' : ''}{change}%
                          </span>
                          <span className="text-xs text-slate-400">{comparisonLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="kpi-change text-slate-500 text-xs">
                    Detected by Google Analytics
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg" style={{ backgroundColor: `${SOURCE_COLORS.ai_referral}20` }}>
                <div className="w-full h-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6" style={{ color: SOURCE_COLORS.ai_referral }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Avg Session Duration */}
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Avg. Session Duration</p>
                <p className="kpi-value">{formatDuration(avgSessionDuration)}</p>
                {showComparison && previousPeriodData?.summary?.avg_session_duration !== undefined ? (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(avgSessionDuration, previousPeriodData.summary.avg_session_duration);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? (
                            <TrendingUp className="w-3 h-3 text-green-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+' : ''}{change}%
                          </span>
                          <span className="text-xs text-slate-400">{comparisonLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="kpi-change text-slate-500 text-xs">
                    {dateFilter?.label || 'Selected period'}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bounce Rate */}
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Bounce Rate</p>
                <p className="kpi-value">{avgBounceRate.toFixed(1)}%</p>
                {showComparison && previousPeriodData?.summary?.avg_bounce_rate !== undefined ? (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(avgBounceRate, previousPeriodData.summary.avg_bounce_rate);
                      // For bounce rate, lower is better, so invert the color logic
                      const isPositive = parseFloat(change) <= 0;
                      return (
                        <>
                          {isPositive ? (
                            <TrendingDown className="w-3 h-3 text-green-600" />
                          ) : (
                            <TrendingUp className="w-3 h-3 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {parseFloat(change) > 0 ? '+' : ''}{change}%
                          </span>
                          <span className="text-xs text-slate-400">{comparisonLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="kpi-change text-slate-500 text-xs">
                    {dateFilter?.label || 'Selected period'}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <Activity className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sources">Traffic Sources</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Sessions by Source - Stacked Area Chart */}
          <Card>
            <CardHeader>
              <CardTitle style={{ color: NAV_DARK_COLOR }}>
                Sessions by Source 
                <span className="text-sm font-normal text-slate-500 ml-2">
                  (by {timeGranularity})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trafficBySource.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={aggregateByGranularity(trafficBySource, timeGranularity)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} stroke={NAV_DARK_COLOR} />
                    <YAxis 
                      tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} 
                      stroke={NAV_DARK_COLOR} 
                      tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                    />
                    <Tooltip 
                      contentStyle={{ color: NAV_DARK_COLOR }}
                      formatter={(value, name) => [isPresentationMode ? maskNumber(value) : value.toLocaleString(), name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())]}
                    />
                    <Legend wrapperStyle={{ color: NAV_DARK_COLOR }} />
                    <Area
                      type="monotone"
                      dataKey="organic"
                      stackId="1"
                      stroke={SOURCE_COLORS.organic}
                      fill={SOURCE_COLORS.organic}
                      name="Organic Search"
                    />
                    <Area
                      type="monotone"
                      dataKey="organic_social"
                      stackId="1"
                      stroke={SOURCE_COLORS.organic_social}
                      fill={SOURCE_COLORS.organic_social}
                      name="Organic Social"
                    />
                    <Area
                      type="monotone"
                      dataKey="ai_referral"
                      stackId="1"
                      stroke={SOURCE_COLORS.ai_referral}
                      fill={SOURCE_COLORS.ai_referral}
                      name="AI Referral"
                    />
                    <Area
                      type="monotone"
                      dataKey="paid"
                      stackId="1"
                      stroke={SOURCE_COLORS.paid}
                      fill={SOURCE_COLORS.paid}
                      name="Paid Search"
                    />
                    <Area
                      type="monotone"
                      dataKey="direct"
                      stackId="1"
                      stroke={SOURCE_COLORS.direct}
                      fill={SOURCE_COLORS.direct}
                      name="Direct"
                    />
                    <Area
                      type="monotone"
                      dataKey="email"
                      stackId="1"
                      stroke={SOURCE_COLORS.email}
                      fill={SOURCE_COLORS.email}
                      name="Email"
                    />
                    <Area
                      type="monotone"
                      dataKey="referral"
                      stackId="1"
                      stroke={SOURCE_COLORS.referral}
                      fill={SOURCE_COLORS.referral}
                      name="Referral"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : trafficOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={aggregateByGranularity(trafficOverTime.map(d => ({...d, organic: d.sessions})), timeGranularity).map(d => ({...d, sessions: d.organic}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} stroke={NAV_DARK_COLOR} />
                    <YAxis 
                      tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} 
                      stroke={NAV_DARK_COLOR}
                      tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                    />
                    <Tooltip 
                      contentStyle={{ color: NAV_DARK_COLOR }}
                      formatter={isPresentationMode ? (value) => [maskNumber(value), 'Sessions'] : undefined}
                    />
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      stroke={chartColors?.[0] || "#00A5A8"}
                      fill={chartColors?.[0] || "#00A5A8"}
                      fillOpacity={0.3}
                      name="Sessions"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <AlertCircle className="w-12 h-12 mb-4 text-slate-300" />
                  <p>No traffic data available for the selected date range.</p>
                  <p className="text-sm">Please connect GA4 or select a different date range.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Source Totals Summary */}
          {sourceTotals && Object.keys(sourceTotals).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.organic }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Organic Search</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.organic || 0))}</p>
                {showComparison && previousSourcesData?.totals?.organic !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.organic || 0, previousSourcesData.totals.organic || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.organic_social }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Organic Social</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.organic_social || 0))}</p>
                {showComparison && previousSourcesData?.totals?.organic_social !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.organic_social || 0, previousSourcesData.totals.organic_social || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.ai_referral }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>AI Referral</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.ai_referral || 0))}</p>
                {showComparison && previousSourcesData?.totals?.ai_referral !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.ai_referral || 0, previousSourcesData.totals.ai_referral || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.paid }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Paid Search</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.paid || 0))}</p>
                {showComparison && previousSourcesData?.totals?.paid !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.paid || 0, previousSourcesData.totals.paid || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.direct }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Direct</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.direct || 0))}</p>
                {showComparison && previousSourcesData?.totals?.direct !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.direct || 0, previousSourcesData.totals.direct || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.email }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Email</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.email || 0))}</p>
                {showComparison && previousSourcesData?.totals?.email !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.email || 0, previousSourcesData.totals.email || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.referral }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Referral</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.referral || 0))}</p>
                {showComparison && previousSourcesData?.totals?.referral !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.referral || 0, previousSourcesData.totals.referral || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS.paid_social }}></div>
                  <span className="text-sm font-medium" style={{ color: NAV_DARK_COLOR }}>Paid Social</span>
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: NAV_DARK_COLOR }}>{maskNumber((sourceTotals.paid_social || 0))}</p>
                {showComparison && previousSourcesData?.totals?.paid_social !== undefined && (
                  <div className="flex items-center gap-1 mt-1">
                    {(() => {
                      const change = calcPercentChange(sourceTotals.paid_social || 0, previousSourcesData.totals.paid_social || 0);
                      const isPositive = parseFloat(change) >= 0;
                      return (
                        <>
                          {isPositive ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
                          <span className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{change}%</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Month-over-Month and Year-over-Year Comparison Table */}
          <TrafficComparisonTable 
            dateFilter={dateFilter}
            excludeTurkey={excludeTurkey}
            selectedRegion={selectedRegion}
            trafficDataSource={trafficDataSource}
          />

          {/* Regional Traffic Tables - EMEA vs NA (not affected by region filter) */}
          <RegionalTrafficTables 
            dateFilter={dateFilter}
            excludeTurkey={excludeTurkey}
            trafficDataSource={trafficDataSource}
          />
        </TabsContent>

        <TabsContent value="sources" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Traffic Sources Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle style={{ color: NAV_DARK_COLOR }}>Traffic Sources Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {sourceTotals && Object.keys(sourceTotals).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Organic Search", value: sourceTotals.organic || 0, fill: SOURCE_COLORS.organic },
                          { name: "Organic Social", value: sourceTotals.organic_social || 0, fill: SOURCE_COLORS.organic_social },
                          { name: "AI Referral", value: sourceTotals.ai_referral || 0, fill: SOURCE_COLORS.ai_referral },
                          { name: "Paid Search", value: sourceTotals.paid || 0, fill: SOURCE_COLORS.paid },
                          { name: "Direct", value: sourceTotals.direct || 0, fill: SOURCE_COLORS.direct },
                          { name: "Email", value: sourceTotals.email || 0, fill: SOURCE_COLORS.email },
                          { name: "Referral", value: sourceTotals.referral || 0, fill: SOURCE_COLORS.referral }
                        ].filter(s => s.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={{ stroke: NAV_DARK_COLOR }}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {[
                          { name: "Organic Search", value: sourceTotals.organic || 0, fill: SOURCE_COLORS.organic },
                          { name: "Organic Social", value: sourceTotals.organic_social || 0, fill: SOURCE_COLORS.organic_social },
                          { name: "AI Referral", value: sourceTotals.ai_referral || 0, fill: SOURCE_COLORS.ai_referral },
                          { name: "Paid Search", value: sourceTotals.paid || 0, fill: SOURCE_COLORS.paid },
                          { name: "Direct", value: sourceTotals.direct || 0, fill: SOURCE_COLORS.direct },
                          { name: "Email", value: sourceTotals.email || 0, fill: SOURCE_COLORS.email },
                          { name: "Referral", value: sourceTotals.referral || 0, fill: SOURCE_COLORS.referral }
                        ].filter(s => s.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ color: NAV_DARK_COLOR }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Traffic Sources Table */}
            <Card>
              <CardHeader>
                <CardTitle style={{ color: NAV_DARK_COLOR }}>Traffic Sources Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sourceTotals && Object.keys(sourceTotals).length > 0 ? (
                    [
                      { name: "Organic Search", value: sourceTotals.organic || 0, color: SOURCE_COLORS.organic },
                      { name: "Organic Social", value: sourceTotals.organic_social || 0, color: SOURCE_COLORS.organic_social },
                      { name: "AI Referral", value: sourceTotals.ai_referral || 0, color: SOURCE_COLORS.ai_referral },
                      { name: "Paid Search", value: sourceTotals.paid || 0, color: SOURCE_COLORS.paid },
                      { name: "Direct", value: sourceTotals.direct || 0, color: SOURCE_COLORS.direct },
                      { name: "Email", value: sourceTotals.email || 0, color: SOURCE_COLORS.email },
                      { name: "Referral", value: sourceTotals.referral || 0, color: SOURCE_COLORS.referral }
                    ].filter(s => s.value > 0).map((source) => (
                      <div key={source.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: source.color }}
                          />
                          <span className="font-medium" style={{ color: NAV_DARK_COLOR }}>{source.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-slate-600 font-semibold">{maskNumber(source.value)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-slate-400 py-8">
                      No traffic source data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WebsiteTrafficPage;
